import React, { useState } from 'react';
import { FUEL_RANGE_CURVE } from '../lib/constants';
import { latestWater, WATER_CAPACITY_L } from '../lib/utils';

const TABS = ['About', 'Specs', 'Layouts'];

// ── Shared ───────────────────────────────────────────────────
function Row({ label, value, sub, right }) {
  if (!value) return null;
  return (
    <div style={{ display:'flex', justifyContent:'space-between', alignItems:'baseline', padding:'7px 0', borderBottom:'1px solid var(--s2)', gap:12 }}>
      <div>
        <div style={{ fontSize:13, color:'var(--slate)' }}>{label}</div>
        {sub && <div style={{ fontFamily:"'IBM Plex Mono',monospace", fontSize:9, color:'var(--slate)', opacity:.7, marginTop:1 }}>{sub}</div>}
      </div>
      <div style={{ textAlign:'right', flexShrink:0 }}>
        <div style={{ fontFamily:"'IBM Plex Mono',monospace", fontSize:13, fontWeight:600, color:'var(--navy)' }}>{value}</div>
        {right && <div style={{ fontFamily:"'IBM Plex Mono',monospace", fontSize:10, color:'var(--slate)', marginTop:1 }}>{right}</div>}
      </div>
    </div>
  );
}

function Dual({ label, m, ft, sub }) {
  if (!m && !ft) return null;
  return (
    <div style={{ display:'flex', justifyContent:'space-between', alignItems:'baseline', padding:'7px 0', borderBottom:'1px solid var(--s2)', gap:12 }}>
      <div>
        <div style={{ fontSize:13, color:'var(--slate)' }}>{label}</div>
        {sub && <div style={{ fontFamily:"'IBM Plex Mono',monospace", fontSize:9, color:'var(--slate)', opacity:.7, marginTop:1 }}>{sub}</div>}
      </div>
      <div style={{ textAlign:'right', flexShrink:0 }}>
        <div style={{ fontFamily:"'IBM Plex Mono',monospace", fontSize:13, fontWeight:600, color:'var(--navy)' }}>{m}</div>
        <div style={{ fontFamily:"'IBM Plex Mono',monospace", fontSize:10, color:'var(--slate)', marginTop:1 }}>{ft}</div>
      </div>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div style={{ background:'var(--surface)', border:'1px solid var(--border)', borderRadius:'var(--r)', padding:'14px 18px', marginBottom:12, boxShadow:'var(--sh)' }}>
      <div style={{ fontFamily:"'IBM Plex Mono',monospace", fontSize:10, fontWeight:700, textTransform:'uppercase', letterSpacing:'.16em', color:'var(--gold)', marginBottom:10, paddingBottom:8, borderBottom:'1px solid var(--border)' }}>{title}</div>
      {children}
    </div>
  );
}

// ── Document status card ─────────────────────────────────────
function DocCard({ title, number, expiry, url, note }) {
  const expired = expiry && new Date(expiry) < new Date();
  const daysLeft = expiry ? Math.ceil((new Date(expiry) - new Date()) / 86400000) : null;
  const soon = daysLeft !== null && daysLeft >= 0 && daysLeft < 90;
  const statusColor = expired ? 'var(--red)' : soon ? '#D97706' : number || expiry ? 'var(--green)' : 'var(--slate)';
  const statusLabel = expired ? 'EXPIRED' : soon ? `${daysLeft}d` : null;

  if (!number && !expiry && !note) return (
    <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', padding:'7px 0', borderBottom:'1px solid var(--s2)' }}>
      <span style={{ fontSize:13, color:'var(--slate)' }}>{title}</span>
      <span style={{ fontFamily:"'IBM Plex Mono',monospace", fontSize:11, color:'var(--border)' }}>—</span>
    </div>
  );

  return (
    <div style={{ padding:'10px 0', borderBottom:'1px solid var(--s2)' }}>
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start' }}>
        <div style={{ flex:1 }}>
          <div style={{ fontSize:13, fontWeight:600, color:'var(--navy)' }}>{title}</div>
          {number && <div style={{ fontFamily:"'IBM Plex Mono',monospace", fontSize:11, color:'var(--slate)', marginTop:2 }}>{number}</div>}
          {note   && <div style={{ fontSize:11, color:'var(--slate)', marginTop:2 }}>{note}</div>}
        </div>
        <div style={{ textAlign:'right', flexShrink:0, marginLeft:12 }}>
          {statusLabel && <div style={{ fontFamily:"'IBM Plex Mono',monospace", fontSize:10, fontWeight:700, color:statusColor, background:`${statusColor}18`, padding:'2px 7px', borderRadius:4, marginBottom:2 }}>{statusLabel}</div>}
          {expiry && <div style={{ fontFamily:"'IBM Plex Mono',monospace", fontSize:10, color:expired?'var(--red)':soon?'#D97706':'var(--slate)' }}>Exp {expiry}</div>}
          {url && <a href={url} target="_blank" rel="noreferrer" style={{ fontFamily:"'IBM Plex Mono',monospace", fontSize:9, color:'var(--gold)', textDecoration:'none', display:'block', marginTop:2 }}>View ↗</a>}
        </div>
      </div>
    </div>
  );
}

// ── Fuel curve & range (curve is single-sourced from constants) ──
// Range = (usable fuel / gph) × boat speed at that RPM.
const MAIN_TANK = 90;
const AUX_TANK  = 38;

function FuelCurve({ tank1Cap, tank2Cap }) {
  const main   = tank1Cap || MAIN_TANK;
  const aux    = tank2Cap || AUX_TANK;
  const total  = main + aux;
  const usableMain  = main  * 0.9;
  const usableTotal = total * 0.9;

  return (
    <div>
      <div style={{ overflowX:'auto' }}>
        <table style={{ width:'100%', borderCollapse:'collapse', minWidth:360 }}>
          <thead>
            <tr>
              {['RPM','kt','gal/hr','L/hr','Main nm','Total nm'].map(h => (
                <th key={h} style={{ fontFamily:"'IBM Plex Mono',monospace", fontSize:8, fontWeight:700, textTransform:'uppercase', letterSpacing:'.08em', color:'var(--slate)', padding:'4px 6px', textAlign: h==='RPM'||h==='kt'?'left':'right', borderBottom:'1px solid var(--border)', whiteSpace:'nowrap' }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {FUEL_RANGE_CURVE.map(({ rpm, gph, kts }) => {
              const lph       = (gph * 3.785).toFixed(1);
              const mainRange  = Math.round((usableMain  / gph) * kts);
              const totalRange = Math.round((usableTotal / gph) * kts);
              const isEco    = rpm <= 2200;
              const isCruise = rpm >= 2400 && rpm <= 2600;
              const rowBg    = isCruise ? 'rgba(184,134,11,.06)' : isEco ? 'rgba(26,107,46,.04)' : 'transparent';
              return (
                <tr key={rpm} style={{ background:rowBg }}>
                  <td style={{ fontFamily:"'IBM Plex Mono',monospace", fontSize:12, fontWeight:700, color:'var(--navy)', padding:'7px 6px', borderBottom:'1px solid var(--s2)' }}>
                    {rpm}
                    {isEco    && <span style={{ marginLeft:4, fontSize:7, background:'rgba(26,107,46,.12)', color:'var(--green)', padding:'1px 4px', borderRadius:3, fontWeight:700 }}>ECO</span>}
                    {isCruise && <span style={{ marginLeft:4, fontSize:7, background:'rgba(184,134,11,.12)', color:'var(--gold)', padding:'1px 4px', borderRadius:3, fontWeight:700 }}>CRUISE</span>}
                  </td>
                  <td style={{ fontFamily:"'IBM Plex Mono',monospace", fontSize:12, fontWeight:600, color:'var(--slate)', padding:'7px 6px', borderBottom:'1px solid var(--s2)', textAlign:'left' }}>{kts}</td>
                  <td style={{ fontFamily:"'IBM Plex Mono',monospace", fontSize:12, fontWeight:600, color:'var(--navy)', padding:'7px 6px', borderBottom:'1px solid var(--s2)', textAlign:'right' }}>{gph}</td>
                  <td style={{ fontFamily:"'IBM Plex Mono',monospace", fontSize:11, color:'var(--slate)', padding:'7px 6px', borderBottom:'1px solid var(--s2)', textAlign:'right' }}>{lph}</td>
                  <td style={{ fontFamily:"'IBM Plex Mono',monospace", fontSize:12, fontWeight:600, color:'var(--navy)', padding:'7px 6px', borderBottom:'1px solid var(--s2)', textAlign:'right' }}>{mainRange}</td>
                  <td style={{ fontFamily:"'IBM Plex Mono',monospace", fontSize:12, fontWeight:700, color: totalRange >= 400 ? 'var(--green)' : 'var(--navy)', padding:'7px 6px', borderBottom:'1px solid var(--s2)', textAlign:'right' }}>{totalRange}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <div style={{ marginTop:10, fontFamily:"'IBM Plex Mono',monospace", fontSize:9, color:'var(--slate)', lineHeight:1.8 }}>
        <div>Main {main} gal · Aux {aux} gal · Total {total} gal · Usable {usableTotal.toFixed(0)} gal (90%)</div>
        <div>Range = (usable fuel ÷ gal/hr) × boat speed · flat water, no current · SO45 shallow keel</div>
      </div>
    </div>
  );
}

export default function BoatTab({ settings = {}, entries = [], engHrs = null, genHrs = null, hullNm: hullNmTotal = null, onSettings }) {
  const [sub, setSub] = useState('About');

  const name    = settings.boat_name   || 'Pardon My French';
  const year    = settings.boat_year   || '2006';
  const model   = settings.boat_model  || 'Sun Odyssey 45';
  const sailNo  = settings.sail_number || '2590';
  const phrf    = settings.phrf        || '144';
  const hailing = settings.hailing_port|| 'Orcas, WA';
  const mmsi    = settings.mmsi        || null;
  const hin     = settings.hin         || 'JS045H506';
  const towUs   = settings.tow_us      || null;
  const syc     = settings.syc_member  || null;
  const marina  = settings.home_marina || 'Elliott Bay Marina';
  const slip    = settings.slip        || 'D-4';
  // Reconciled against the logbook in App.jsx: engine/gen are odometers
  // (highest reading ever logged), hull nm is baseline + every logged trip.
  const engBase = settings.engine_baseline || 2940;
  const genBase = settings.gen_baseline    || 1130;
  const engNow  = engHrs || engBase;
  const genNow  = genHrs || genBase;
  const hullNow = hullNmTotal != null ? hullNmTotal : (Number(settings.hull_nm_baseline) || 0);
  const loggedNm = Math.round(entries.reduce((s2,e)=>s2+(Number(e.nm)||0),0));

  // Fresh water: latest gauge reading pulled from the logbook.
  const WATER_CAP = WATER_CAPACITY_L;
  const water = latestWater(entries);

  const tabStyle = t => ({
    fontFamily:"'IBM Plex Mono',monospace", fontSize:11, fontWeight:700,
    letterSpacing:'.08em', textTransform:'uppercase',
    padding:'10px 16px', border:'none', background:'none',
    color: sub===t ? 'var(--gold)' : 'var(--slate)',
    borderBottom: sub===t ? '2px solid var(--gold)' : '2px solid transparent',
    marginBottom:-1, cursor:'pointer', transition:'color .18s, border-color .18s',
  });

  return (
    <div>
      <div style={{ display:'flex', borderBottom:'1px solid var(--border)', marginBottom:20 }}>
        {TABS.map(t => <button key={t} style={tabStyle(t)} onClick={()=>setSub(t)}>{t}</button>)}
      </div>

      {/* ══ ABOUT ══ */}
      {sub === 'About' && (
        <>
          {/* Identity card with the boat profile */}
          <div style={{ background:'#fff', border:'1px solid var(--border)', borderRadius:'var(--r)', padding:'18px 20px 10px', marginBottom:14, boxShadow:'var(--sh)', position:'relative' }}>
            {onSettings && (
              <button onClick={onSettings} title="Settings" style={{ position:'absolute', top:12, right:12, width:32, height:32, border:'1px solid var(--border)', borderRadius:8, background:'var(--s2)', cursor:'pointer', display:'flex', alignItems:'center', justifyContent:'center', color:'var(--slate)', padding:0, zIndex:2 }}>
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="3"/>
                <path d="M19.4 15a1.65 1.65 0 00.33 1.82l.06.06a2 2 0 010 2.83 2 2 0 01-2.83 0l-.06-.06a1.65 1.65 0 00-1.82-.33 1.65 1.65 0 00-1 1.51V21a2 2 0 01-4 0v-.09A1.65 1.65 0 009 19.4a1.65 1.65 0 00-1.82.33l-.06.06a2 2 0 01-2.83-2.83l.06-.06A1.65 1.65 0 004.68 15a1.65 1.65 0 00-1.51-1H3a2 2 0 010-4h.09A1.65 1.65 0 004.6 9a1.65 1.65 0 00-.33-1.82l-.06-.06a2 2 0 012.83-2.83l.06.06A1.65 1.65 0 009 4.68a1.65 1.65 0 001-1.51V3a2 2 0 014 0v.09a1.65 1.65 0 001 1.51 1.65 1.65 0 001.82-.33l.06-.06a2 2 0 012.83 2.83l-.06.06A1.65 1.65 0 0019.4 9a1.65 1.65 0 001.51 1H21a2 2 0 010 4h-.09a1.65 1.65 0 00-1.51 1z"/>
              </svg>
            </button>
            )}
            <div style={{ minWidth:0 }}>
              <div style={{ fontFamily:"'IBM Plex Mono',monospace", fontSize:10, fontWeight:700, letterSpacing:'.18em', textTransform:'uppercase', color:'var(--gold)', marginBottom:3 }}>S/V · {marina}</div>
              <div style={{ fontFamily:"'Caveat',cursive", fontSize:28, color:'var(--navy)', lineHeight:1.15, marginBottom:5 }}>{name}</div>
              <div style={{ fontSize:13, color:'var(--ink-s)', lineHeight:1.9 }}>
                <div>{year} Jeanneau {model}</div>
                <div>Sail #{sailNo} · PHRF {phrf} · CE Cat A</div>
                {mmsi && <div style={{ fontFamily:"'IBM Plex Mono',monospace", fontSize:12 }}>MMSI {mmsi}</div>}
                <div>{marina} · Slip {slip}</div>
              </div>
            </div>
            <img src="/pmf-profile.png" alt="" loading="lazy" decoding="async" style={{ width:'100%', maxWidth:290, height:'auto', display:'block', margin:'8px auto 0' }}/>
          </div>

          {/* Key identifiers */}
          <Section title="Vessel Identifiers">
            <Row label="Sail Number"     value={sailNo}/>
            <Row label="PHRF Rating"     value={phrf}/>
            <Row label="CE Category"     value="A — Offshore"/>
            <Row label="Hailing Port"    value={hailing}/>
            {mmsi  && <Row label="MMSI"            value={mmsi}/>}
            <Row label="Hull ID (HIN)"   value={hin}/>
            {towUs && <Row label="Tow US / BoatUS" value={towUs}/>}
            {syc   && <Row label="SYC Member #"    value={syc}/>}
            <Row label="Home Marina"     value={marina}/>
            <Row label="Slip"            value={slip}/>
          </Section>

          {/* Documentation & Registration */}
          <Section title="Documentation &amp; Registration">
            <DocCard
              title="USCG Documentation"
              number={settings.doc_uscg_number}
              expiry={settings.doc_uscg_expiry}
              url={settings.doc_uscg_url}
            />
            <DocCard
              title="WA State Registration"
              number={settings.doc_wa_reg_number}
              expiry={settings.doc_wa_reg_expiry}
              url={settings.doc_wa_reg_url}
            />
            <DocCard
              title="Canadian Pleasure Craft Licence"
              number={settings.doc_canada_number}
              expiry={settings.doc_canada_expiry}
              url={settings.doc_canada_url}
            />
            {!settings.doc_uscg_number && !settings.doc_wa_reg_number && !settings.doc_canada_number && (
              <div style={{ padding:'10px 0', fontSize:12, color:'var(--slate)' }}>
                No documentation on file. Add in <button onClick={onSettings} style={{ background:'none', border:'none', color:'var(--gold)', fontSize:12, cursor:'pointer', padding:0, fontWeight:600 }}>Settings → Safety & Docs</button>
              </div>
            )}
          </Section>

          {/* Insurance */}
          <Section title="Insurance &amp; Assistance">
            <DocCard
              title="Vessel Insurance"
              number={settings.doc_insurance_company ? `${settings.doc_insurance_company}${settings.doc_insurance_policy?' · '+settings.doc_insurance_policy:''}` : null}
              expiry={settings.doc_insurance_expiry}
              url={settings.doc_insurance_url}
            />
            <DocCard
              title="Tow US / BoatUS"
              number={towUs}
              note={!towUs ? null : undefined}
            />
            {!settings.doc_insurance_company && !towUs && (
              <div style={{ padding:'10px 0', fontSize:12, color:'var(--slate)' }}>
                No insurance info on file. Add in <button onClick={onSettings} style={{ background:'none', border:'none', color:'var(--gold)', fontSize:12, cursor:'pointer', padding:0, fontWeight:600 }}>Settings → Safety & Docs</button>
              </div>
            )}
          </Section>

          {/* Safety gear */}
          <Section title="Safety Equipment">
            <DocCard
              title="EPIRB"
              number={settings.epirb_brand ? `${settings.epirb_brand}${settings.epirb_id?' · '+settings.epirb_id:''}` : null}
              expiry={settings.epirb_expiry}
            />
            <DocCard
              title="Liferaft"
              number={settings.liferaft_brand ? `${settings.liferaft_brand}${settings.liferaft_capacity?' · '+settings.liferaft_capacity:''}` : null}
              expiry={settings.liferaft_service_expiry}
            />
            {settings.plb_ids && <Row label="PLBs" value={settings.plb_ids}/>}
          </Section>

          {/* Engine baselines */}
          <Section title="Engine &amp; Hours">
            <Row label="Engine" value="Yanmar 4JH3-TE · 75 hp"/>
            <Row label="Engine Hours" value={`${Number(engNow).toLocaleString()}h`}/>
            <Row label="Generator" value="Northern Lights 6KW"/>
            <Row label="Generator Hours" value={`${Number(genNow).toLocaleString()}h`}/>
            <Row label="Hull Distance" value={loggedNm > 0 ? `${hullNow.toLocaleString()} nm (${(hullNow-loggedNm).toLocaleString()} + ${loggedNm.toLocaleString()} logged)` : `${hullNow.toLocaleString()} nm`}/>
          </Section>

          <Section title="Fresh Water">
            {water ? (
              <div style={{ padding:'4px 0 2px' }}>
                <div style={{ display:'flex', justifyContent:'space-between', alignItems:'baseline', marginBottom:9 }}>
                  <span>
                    <span style={{ fontFamily:"'IBM Plex Mono',monospace", fontSize:22, fontWeight:600, color:'var(--navy)' }}>{water.remaining}</span>
                    <span style={{ fontFamily:"'IBM Plex Mono',monospace", fontSize:12, color:'var(--slate)' }}> / {WATER_CAP} L</span>
                  </span>
                  <span style={{ fontFamily:"'Plus Jakarta Sans',sans-serif", fontSize:12, color:'var(--slate)' }}>{water.toppedOff ? 'Tanks full' : `${water.used} L used`}</span>
                </div>
                <div style={{ height:8, borderRadius:4, background:'var(--tint, #FAF8F4)', overflow:'hidden' }}>
                  <div style={{ height:'100%', width:`${100 - water.pct}%`, background: water.pct >= 85 ? 'var(--red)' : '#2b6fb0', borderRadius:4, transition:'width .2s' }}/>
                </div>
                <div style={{ fontFamily:"'IBM Plex Mono',monospace", fontSize:10, color:'var(--slate)', marginTop:11 }}>
                  Latest reading {water.date || ''}{water.location ? ` · ${water.location}` : ''}
                </div>
              </div>
            ) : (
              <div style={{ padding:'8px 0', fontFamily:"'Plus Jakarta Sans',sans-serif", fontSize:13, color:'var(--slate)' }}>
                No water readings yet. Add the gauge reading on any log entry, under Tanks &amp; Batt. Combined capacity {WATER_CAP} L (210 fore + 235 aft).
              </div>
            )}
          </Section>
        </>
      )}
      {sub === 'Specs' && (
        <>
          <Section title="Dimensions">
            <Dual label="LOA"                   m="13.72 m"  ft="45' 0&quot;"/>
            <Dual label="LWL"                   m="11.61 m"  ft="38' 1&quot;"/>
            <Dual label="Beam"                  m="4.37 m"   ft="14' 4&quot;"/>
            <Dual label="Draft — Shallow Keel"  m="1.64 m"   ft="5' 4&quot;" sub="PMF configuration"/>
            <Dual label="Draft — Standard Keel" m="2.05 m"   ft="6' 9&quot;"/>
            <Dual label="Air Draft"             m="19.6 m"   ft="64' 4&quot;"/>
            <Row  label="Displacement"          value="9,900 kg" right="21,825 lbs"/>
            <Row  label="Ballast"               value="3,160 kg" right="6,967 lbs"/>
            <Row  label="Ballast Ratio"         value="31.9%"/>
            <Row  label="CE Design Category"    value="A — Offshore"/>
            <Row  label="Designer"              value="Philippe Briand"/>
            <Row  label="Builder"               value="Jeanneau · Les Herbiers, France"/>
            <Row  label="Model Year"            value={year}/>
          </Section>

          <Section title="Rig &amp; Sails">
            <Row  label="Rig Type"              value="Sloop — in-mast furling main"/>
            <Row  label="Mast"                  value="Z Spars aluminium"/>
            <Dual label="P — Main luff"         m="15.80 m"  ft="51' 10&quot;"/>
            <Dual label="E — Main foot"         m="5.30 m"   ft="17' 5&quot;"/>
            <Dual label="I — Forestay height"   m="16.85 m"  ft="55' 3&quot;"/>
            <Dual label="J — Forestay base"     m="4.69 m"   ft="15' 5&quot;"/>
            <Row  label="SA/D Ratio"            value="20.4"/>
            <Row  label="Main"                  value="Ballard Tri-Radial Contender" right="2017"/>
            <Row  label="Genoa"                 value="Ballard 135% Warp-Drive" right="2016"/>
            <Row  label="Spinnaker"             value="Hyde Asymmetric A2" right="Snuffer sock"/>
            <Row  label="PHRF Rating"           value={phrf}/>
          </Section>

          <Section title="Propulsion &amp; Mechanical">
            <Row label="Engine"           value="Yanmar 4JH3-TE"/>
            <Row label="Type"             value="4-stroke turbo diesel · direct injection"/>
            <Row label="Max Output"       value="75 hp (55 kW) @ 3,800 RPM"/>
            <Row label="Continuous"       value="69 hp (51 kW) @ 3,700 RPM"/>
            <Row label="Displacement"     value="1.995 L (122 cu in)"/>
            <Row label="Bore × Stroke"    value="84 × 90 mm"/>
            <Row label="Cylinders"        value="4"/>
            <Row label="Aspiration"       value="Turbocharged"/>
            <Row label="Dry Weight"       value="219 kg (483 lbs)"/>
            <Row label="Propeller"        value="MaxProp 3-blade feathering"/>
            <Row label="Drive"            value="Shaft / direct drive"/>
            <Row label="Bow Thruster"     value="Lewmar 12V hydraulic"/>
            <Row label="Generator"        value="Northern Lights 6KW diesel"/>
            <Row label="Alternator"       value="90A Balmar / external reg"/>
            <Row label="Engine Hours"     value={`${Number(engNow).toLocaleString()}h`}/>
          </Section>

          <Section title="Tanks &amp; Capacities">
            <Row label="Fuel Main"      value={`${settings.tank1_cap||90} gal`} right={`${Math.round((settings.tank1_cap||90)*3.785)} L`}/>
            <Row label="Fuel Aux"       value={`${settings.tank2_cap||38} gal`} right={`${Math.round((settings.tank2_cap||38)*3.785)} L`}/>
            <Row label="Fuel Total"     value={`${(settings.tank1_cap||90)+(settings.tank2_cap||38)} gal`} right={`${Math.round(((settings.tank1_cap||90)+(settings.tank2_cap||38))*3.785)} L`}/>
            <Row label="Water Aft"      value={`${settings.water_aft_cap||240} L`} right={`${Math.round((settings.water_aft_cap||240)/3.785)} gal`}/>
            <Row label="Water Fore"     value={`${settings.water_fore_cap||235} L`} right={`${Math.round((settings.water_fore_cap||235)/3.785)} gal`}/>
            <Row label="Holding Tank"   value="20 gal" right="76 L"/>
          </Section>

          <Section title="Electronics">
            {[
              'Raymarine Axiom 9" — cockpit MFD',
              'Raymarine Axiom 12" — nav station',
              'Raymarine S3G Autopilot + Type 2 hydraulic',
              'Vesper WatchMate 8000 AIS (class B)',
              'Icom IC-M424G VHF w/ GPS',
              'Icom IC-M802 SSB radio',
              'Orca Navigation (iPad + Bluetooth AIS)',
              'Starlink — maritime flat dish',
              'Victron BMV-712 battery monitor',
              'Victron Cerbo GX energy manager',
              'Victron SmartSolar MPPT 100/30',
              '4× 200W solar panels (800W total)',
              'Balmar 90A alternator + MC-618 regulator',
            ].map(item => (
              <div key={item} style={{ fontSize:13, color:'var(--ink-s)', padding:'5px 0', borderBottom:'1px solid var(--s2)' }}>{item}</div>
            ))}
          </Section>

          <Section title="Yanmar 4JH3TE — Fuel Curve &amp; Range">
            <FuelCurve tank1Cap={settings.tank1_cap} tank2Cap={settings.tank2_cap}/>
          </Section>

          {/* Polar diagram */}
          <div style={{ background:'var(--surface)', border:'1px solid var(--border)', borderRadius:'var(--r)', padding:'14px 18px', marginBottom:12, boxShadow:'var(--sh)' }}>
            <div style={{ fontFamily:"'IBM Plex Mono',monospace", fontSize:10, fontWeight:700, textTransform:'uppercase', letterSpacing:'.16em', color:'var(--gold)', marginBottom:10, paddingBottom:8, borderBottom:'1px solid var(--border)' }}>Sun Odyssey 45 — Polar Diagram</div>
            <img src="/boat-polar.jpg" alt="SO45 polar diagram" style={{ width:'100%', borderRadius:8 }}/>
          </div>
        </>
      )}

      {/* ══ LAYOUTS ══ */}
      {sub === 'Layouts' && (
        <>
          <div style={{ background:'var(--surface)', border:'1px solid var(--border)', borderRadius:'var(--r)', padding:'14px 18px', marginBottom:12, boxShadow:'var(--sh)' }}>
            <div style={{ fontFamily:"'IBM Plex Mono',monospace", fontSize:10, fontWeight:700, textTransform:'uppercase', letterSpacing:'.16em', color:'var(--gold)', marginBottom:10, paddingBottom:8, borderBottom:'1px solid var(--border)' }}>Deck Plan</div>
            <img src="/layout-deck.png" alt="Deck layout" style={{ width:'100%', borderRadius:8 }}/>
          </div>
          <div style={{ background:'var(--surface)', border:'1px solid var(--border)', borderRadius:'var(--r)', padding:'14px 18px', marginBottom:12, boxShadow:'var(--sh)' }}>
            <div style={{ fontFamily:"'IBM Plex Mono',monospace", fontSize:10, fontWeight:700, textTransform:'uppercase', letterSpacing:'.16em', color:'var(--gold)', marginBottom:10, paddingBottom:8, borderBottom:'1px solid var(--border)' }}>Interior Layout</div>
            <img src="/layout-interior.png" alt="Interior layout" style={{ width:'100%', borderRadius:8 }}/>
          </div>
          <div style={{ borderRadius:'var(--r)', overflow:'hidden', marginBottom:12, boxShadow:'var(--sh)' }}>
            <img src="/hero.jpg" alt="PMF cockpit" style={{ width:'100%', objectFit:'cover', maxHeight:280, display:'block' }}/>
          </div>
        </>
      )}
    </div>
  );
}
